import * as React from 'react';
import { showFavoritesFoldersList, showModalCreateFolder } from '@cian/favorites-assign-widget';
import { hideNotification } from '@cian/favorites-assign-widget/es/shared/actions';
import { useDispatch, useSelector } from 'react-redux';

import { EAlertType, requestAuthentication } from '../../actions/authentication';
import { FavoriteButton, FavoriteTooltip } from '../../components/RedesignedFavoriteButtonDesktop';
import { addToFavorite, deleteFromFavorite } from '../../thunk/favorites';
import { selectIsAuthenticated } from '../../selectors/user';
import { selectRecommendationById } from '../../selectors/recommendations/selectRecommendationById';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { trackFavoritesLinkClick } from './tracking';

const TOOLTIP_CLOSE_TIMEOUT = 6000;

type TRedesignedFavoriteButtonDesktopContainerProps = {
  offerId: number;
};

export const RedesignedFavoriteButtonDesktopContainer: React.FC<TRedesignedFavoriteButtonDesktopContainerProps> = ({
  offerId,
}) => {
  const dispatch = useDispatch<TThunkDispatch>();
  const buttonRef = React.useRef<HTMLDivElement>(null);
  const recommendation = useSelector(selectRecommendationById(offerId));
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { savedOfferId, showNotification, userHasFolders } = useSelector(
    (state: IApplicationState) => state.favoritesAssignWidget,
  );

  const isTooltipOpen = Boolean(
    recommendation.offerId && showNotification && savedOfferId.length === 1 && savedOfferId[0] === offerId,
  );

  React.useEffect(() => {
    if (!isTooltipOpen) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      dispatch(hideNotification());
    }, TOOLTIP_CLOSE_TIMEOUT);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [dispatch, isTooltipOpen]);

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();

      if (!recommendation.offerId) {
        return;
      }

      if (recommendation.isFavorite) {
        void dispatch(deleteFromFavorite(recommendation)).catch(() => undefined);

        return;
      }

      void dispatch(addToFavorite(recommendation)).catch(() => undefined);
    },
    [dispatch, recommendation],
  );

  const handleWrapperClick = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const handleCloseTooltip = React.useCallback(() => {
    dispatch(hideNotification());
  }, [dispatch]);

  const handleAddToFolder = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();

      dispatch(hideNotification());

      if (!recommendation.offerId) {
        return;
      }

      if (!isAuthenticated) {
        dispatch(requestAuthentication({ alert: EAlertType.Favorites }));

        return;
      }

      if (userHasFolders) {
        dispatch(showFavoritesFoldersList({ offerIds: [recommendation.offerId] }));

        return;
      }

      dispatch(showModalCreateFolder({ offerIds: [recommendation.offerId] }));
    },
    [dispatch, isAuthenticated, recommendation.offerId, userHasFolders],
  );

  const handleFavoritesLinkClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation();
      trackFavoritesLinkClick(offerId);
    },
    [offerId],
  );

  if (!recommendation.offerId) {
    return null;
  }

  return (
    <div onClick={handleWrapperClick}>
      <div ref={buttonRef}>
        <FavoriteButton isActive={recommendation.isFavorite} onClick={handleClick} />
      </div>
      <FavoriteTooltip
        anchorRef={buttonRef}
        isOpen={isTooltipOpen}
        onAddToFolder={handleAddToFolder}
        onClose={handleCloseTooltip}
        onFavoritesLinkClick={handleFavoritesLinkClick}
      />
    </div>
  );
};
